import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import type { AnnotationLinkStyle } from 'balus-common';
import { AnnotationLinkStyles } from 'balus-common';
import { classNames } from '@framework/utils';
import { usePopupRef } from '@framework/hooks';
import { Point } from '@view-model/models/common/basic';
import { AnnotationLinkShape } from './AnnotationLinkShape';
import { straightBezier } from './straightBezier';
import { AnnotationLinkStyleI18nKeys } from './AnnotationLinkStyleI18nKeys';

type Props = {
  onStyleSelected(style: AnnotationLinkStyle): void;
};

export const AnnotationLinkStyleMenuButton: React.FC<Props> = ({ onStyleSelected }: Props) => {
  const { t } = useTranslation();
  const { isOpen, popupRef, handleToggle } = usePopupRef<HTMLDivElement>(false);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      handleToggle();
    },
    [handleToggle]
  );

  return (
    <div className="relative hover:bg-gray-200">
      <button
        // data-right-click-menu は親 item をクリックしても閉じないようにするためのマーカー
        data-right-click-menu
        className="flex w-full cursor-pointer px-8 py-4"
        onClick={handleClick}
      >
        <div className="inline-block pl-4 pr-6">{t('canvas.menu.lineStyle')}</div>
        <FontAwesomeIcon icon={faAngleRight} className="ml-auto" />
      </button>
      <div
        className={classNames(
          'absolute -right-32 top-0 z-50 w-32 rounded-lg bg-white py-4 shadow-2xl',
          isOpen ? 'block' : 'hidden'
        )}
        ref={popupRef}
      >
        {AnnotationLinkStyles.map((style) => (
          <button
            key={style}
            className="flex w-full cursor-pointer px-8 py-4 hover:bg-gray-300"
            title={t(AnnotationLinkStyleI18nKeys[style])}
            onClick={() => onStyleSelected(style)}
          >
            <svg viewBox="0 0 32 32">
              <AnnotationLinkShape
                style={style}
                bezier={straightBezier(new Point(4, 16), new Point(28, 16))}
                reversed={false}
                color="#aaa"
                thickness={2}
              />
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
};
